/**
 * Platform detection для PWA-tracking'а (Яндекс.Метрика).
 *
 * Единая точка правды: UA-регексы + display-mode проверки жили inline в
 * `useInstallPrompt.ts` и `usePushEnrollment.ts`. Выделены сюда чтобы
 * `reachPwaGoal` в `ym.ts` мог навесить `{platform, standalone}` на каждое
 * событие без импорта хуков (хук вызывается только внутри React-компонента).
 *
 * Priority при определении platform:
 *   1. Yandex Browser (iOS/Android обёртка) → `'yandex'` — install создаёт
 *      shortcut, не полноценный PWA; отдельный bucket для честной статистики.
 *   2. iOS (iPhone/iPad/iPod, НЕ IE Mobile) → `'ios'`.
 *   3. Android UA → `'android'`.
 *   4. Остальное (desktop Chrome/Edge/Safari/Firefox) → `'desktop'`.
 */

export type PwaPlatform = 'ios' | 'android' | 'desktop' | 'yandex'

export interface PwaContext {
  platform: PwaPlatform
  standalone: boolean
  /** "16.4" для iOS Safari 16.4+; undefined если не определилось или не iOS */
  iosVersion?: string
}

export function detectIOS(): boolean {
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window)
}

export function detectAndroid(): boolean {
  if (typeof navigator === 'undefined') return false
  return /Android/.test(navigator.userAgent)
}

export function detectYandexBrowser(): boolean {
  if (typeof navigator === 'undefined') return false
  return /YaBrowser|YaSearchBrowser|YandexBrowser/i.test(navigator.userAgent)
}

export function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

/** "16.4" если iOS Safari 16.4+, undefined иначе. */
export function detectIOSVersion(): string | undefined {
  if (typeof navigator === 'undefined') return undefined
  const match = navigator.userAgent.match(/OS (\d+)_(\d+)/)
  if (!match) return undefined
  return `${match[1]}.${match[2]}`
}

export function getPwaPlatform(): PwaPlatform {
  if (detectYandexBrowser()) return 'yandex'
  if (detectIOS()) return 'ios'
  if (detectAndroid()) return 'android'
  return 'desktop'
}

export function getPwaContext(): PwaContext {
  const platform = getPwaPlatform()
  const ctx: PwaContext = {
    platform,
    standalone: detectStandalone(),
  }
  if (platform === 'ios') {
    const v = detectIOSVersion()
    if (v) ctx.iosVersion = v
  }
  return ctx
}
